import { useEffect, useRef, useState } from "react";
import { api, errorMessage, formatCode, SESSION_KEY } from "../api";

interface Props {
  token: string;
  onFinished: (
    code: string | null,
    options?: { showScanner?: boolean },
  ) => void;
}

type State =
  | { phase: "linking" }
  | { phase: "confirm"; code: string }
  | { phase: "done"; code: string }
  | { phase: "error"; message: string };

export function PairConfirm({ token, onFinished }: Props) {
  const [state, setState] = useState<State>({ phase: "linking" });
  const started = useRef(false);

  useEffect(() => {
    // Der Pairing-Token ist einmal verwendbar – nur ein Versuch pro Aufruf.
    if (started.current) return;
    started.current = true;

    void (async () => {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        try {
          await api.getSession(stored);
          setState({ phase: "confirm", code: stored });
          return;
        } catch {
          localStorage.removeItem(SESSION_KEY);
        }
      }
      // Keine aktive Session auf diesem Gerät: automatisch eine neue
      // starten und den wartenden Rechner direkt hineinholen.
      try {
        const session = await api.createSession();
        await api.completePairing(token, session.code);
        localStorage.setItem(SESSION_KEY, session.code);
        // Direkt scanbereit bleiben, um das nächste Gerät zu verbinden
        onFinished(session.code, { showScanner: true });
      } catch (err) {
        setState({ phase: "error", message: errorMessage(err) });
      }
    })();
  }, [token, onFinished]);

  async function confirm(code: string) {
    setState({ phase: "linking" });
    try {
      await api.completePairing(token, code);
      setState({ phase: "done", code });
    } catch (err) {
      setState({ phase: "error", message: errorMessage(err) });
    }
  }

  return (
    <main className="page narrow">
      <header className="hero">
        <h1>📦 Share</h1>
      </header>

      <section className="card center">
        {state.phase === "linking" && <p className="muted">Verbinde …</p>}

        {state.phase === "confirm" && (
          <>
            <h2>Gerät verbinden?</h2>
            <p className="muted">
              Möchtest du den Rechner, der diesen QR-Code anzeigt, mit deiner
              Session <strong>{formatCode(state.code)}</strong> verbinden?
            </p>
            <div className="btn-row">
              <button className="btn primary" onClick={() => confirm(state.code)}>
                Ja, verbinden
              </button>
              <button className="btn ghost" onClick={() => onFinished(state.code)}>
                Abbrechen
              </button>
            </div>
          </>
        )}

        {state.phase === "done" && (
          <>
            <h2>✅ Gerät verbunden</h2>
            <p className="muted">
              Der andere Rechner tritt der Session{" "}
              <strong>{formatCode(state.code)}</strong> jetzt automatisch bei.
            </p>
            <button className="btn primary" onClick={() => onFinished(state.code)}>
              Zurück zur Session
            </button>
          </>
        )}

        {state.phase === "error" && (
          <>
            <h2>Verbinden fehlgeschlagen</h2>
            <div className="banner banner-error">{state.message}</div>
            <button className="btn primary" onClick={() => onFinished(null)}>
              Zur Startseite
            </button>
          </>
        )}
      </section>
    </main>
  );
}
